/**
 * Adaptive stage v2 — THE single source of truth for how the fixed-height design
 * stage maps onto a device viewport, and how the signed three-column composition
 * (left rail · board · right rail) reflows across the full aspect-ratio range.
 *
 * TWO consumers share these numbers and MUST stay in lock-step:
 *   1. The no-flash inline `fit()` script in `index.html`. It runs before the TS
 *      bundle loads (otherwise the un-scaled 1600×900 stage flashes top-left), so
 *      it cannot import this module — it hand-mirrors the CORE constants + the
 *      rail/fit math below. `tests/utils/stageLayout.indexHtmlMirror.test.ts`
 *      guards that mirror: change a constant here and the test fails until the
 *      inline copy matches.
 *   2. TS runtime + tests (the UI-scale legibility floor at boot, unit tests),
 *      which import these pure functions directly.
 *
 * ── Why an adaptive stage (the iPad/iPhone "premium fit") ────────────────────
 * The board is locked to a 10:7 aspect (GRID 20×14 × CELL 48 = 960×672) and is
 * fit-to-contain + centered in the middle column. The *signed* desktop
 * composition (docs/gameboard_mockup.html: 282 / 1fr / 252) looks great near
 * 16:9, but its FIXED-px rails are the bottleneck off that aspect:
 *   • 4:3 iPad (~1.33): the stage is only 1200 design-px wide, so 534px of rails
 *     eat 44% of the width → the board is width-limited and leaves large top/
 *     bottom letterbox bands (the reported "empty space").
 *   • ultrawide phone (~2.17): the board fills vertically but leaves left/right
 *     slack, and the rails are tight for the HUD on a small physical screen.
 * Same root cause, opposite symptom: one rigid composition can't be optimal from
 * 1.33 → 2.2. The fix is {@link computeRailWidths}: a piecewise-linear rail width
 * anchored at the signed widths at {@link REF_ASPECT}, shrinking toward a
 * readable MIN as aspect → {@link MIN_ASPECT} (recovers the board on iPad) and
 * growing toward MAX as aspect → {@link MAX_ASPECT} (absorbs phone side-slack +
 * grants the HUD more px). Whatever letterbox remains is dressed by the CSS bezel
 * housing (`#canvas-row` in index.html) so it reads as deliberate hardware frame,
 * never void. No grid/gameplay dimension changes — zero balance/determinism risk.
 *
 * ── Build 62 follow-up: full-bleed ("randlos") ──────────────────────────────
 * The owner reported the phone game reading as a small "window" inside a thick
 * dark frame and asked for a true 100% edge-to-edge fill — INCLUDING under the
 * notch / Dynamic Island / home indicator. The fit therefore reserves NOTHING
 * for the safe area: the stage fills the entire `viewport-fit=cover` viewport
 * (`vw × vh`), so the only margin that can ever remain is the aspect letterbox
 * when the device sits outside the [{@link MIN_ASPECT}, {@link MAX_ASPECT}] band
 * (e.g. a 4:3 iPad), and that residual is dressed by the `#canvas-row` bezel.
 *
 * Consequence the owner accepted: on a notched iPhone the physical cutout now
 * overlaps whatever HUD content sits at that screen edge. The real per-side
 * `env(safe-area-inset-*)` are still PUBLISHED on `window.__stageFit.insets`
 * (for any future thumb-zone / notch-aware placement) but are no longer
 * subtracted from the stage geometry.
 */

/** Fixed design height; the width follows the (clamped) device aspect. */
export const DESIGN_H = 900;
/** Aspect floor — at/below this the rails are at MIN (4:3 iPad ≈ 1.333 > 1.3). */
export const MIN_ASPECT = 1.3;
/** Aspect ceiling — at/above this the rails are at MAX (ultrawide phone ≈ 2.16). */
export const MAX_ASPECT = 2.2;
/** The signed reference aspect (16:9) where rails equal their mockup widths. */
export const REF_ASPECT = 16 / 9;

/** Signed mockup rail widths (docs/gameboard_mockup.html: 282 / 1fr / 252). */
export const RAIL_REF_LEFT = 282;
export const RAIL_REF_RIGHT = 252;
/**
 * Narrow-aspect floor — the rail width as aspect → {@link MIN_ASPECT} (4:3 iPad).
 * Lowered from 210/200 so the centre column (and thus the fit-to-contain board)
 * recovers more width on the iPad — the owner-requested "bigger iPad grid". The
 * deploy/dossier rails stay readable here: the longest specimen names already
 * wrap inside the carded rows at the previous floor, so the few recovered px only
 * shift where they wrap, never clipping. ONLY the iPad end moves — the 16:9
 * reference ({@link RAIL_REF_LEFT}) and the ultrawide-phone ceiling
 * ({@link RAIL_MAX_LEFT}) are untouched, so desktop + iPhone are unchanged.
 */
export const RAIL_MIN_LEFT = 176;
export const RAIL_MIN_RIGHT = 168;
/**
 * Wide-aspect ceiling — absorbs phone side-slack + grants the HUD more px.
 * CR-6 (Build QA): raised 360/322 → 401/363. On the ~2.16 wide-phone aspect the
 * board is HEIGHT-bound, so the centre column was wider than the 10:7 board and
 * left a near-black `#canvas-row` margin on each side of the grid — the owner's
 * reported "black stripe between grid and HUD". Widening the wide-aspect rails
 * shrinks the centre column toward the board's fit-width so the stripe closes
 * (and hands the side HUDs more room for the comms TV / deploy rail). ONLY the
 * MAX (ultrawide/phone) end moves — the 4:3 iPad sits at RAIL_MIN and 16:9
 * desktop at RAIL_REF, both untouched, so the rail geometry self-gates to phones.
 *
 * Task-013 (AC-17/O-1): RAIL_MAX_LEFT raised 401 → 412 — the moderate,
 * phone-aspect-only correction Strategy A calls for (options paper §4.2): a
 * few more design-px for the left column at the ultrawide-phone end, so the
 * commentary TV (docked at its base, `#comms-dock`) has a touch more width to
 * work with before it and the deploy rail above start reading tight on a real
 * iPhone. ONLY the ultrawide ceiling moves; RAIL_MIN/RAIL_REF (iPad/desktop)
 * and the right rail are untouched — the correction self-gates to phones.
 */
/**
 * BL-290 (HUD rails premium pass) — the two ultrawide ceilings are REBALANCED
 * against each other, not raised: LEFT 412 → 452, RIGHT 363 → 323. Their SUM is
 * unchanged (775), so the centre column — and therefore the board — is
 * byte-identical at every aspect. Why this direction: the two rails have
 * opposite scarcities, measured on the real device profiles. The LEFT rail is
 * horizontally starved (a full-height Dynamic-Island reserve of 135 design px
 * eats 35 % of its content width on the 852×393 target, and `#hud-left` clips
 * rather than scrolls) while being vertically full to the pixel. The RIGHT rail
 * is the mirror image: ~270 design px of unused HEIGHT, and width it does not
 * need — a deploy bay is icon + two short text lines, never a wide element.
 * Trading 37 px of the right rail's surplus width into the left rail's deficit
 * costs the board nothing and is the only lever that does. As with the Task-013
 * change above, ONLY the ultrawide ceiling moves, so iPad (RAIL_MIN) and 16:9
 * desktop (RAIL_REF) are untouched and this self-gates to phones.
 */
export const RAIL_MAX_LEFT = 452;
export const RAIL_MAX_RIGHT = 323;

/**
 * UI-scale legibility floor (mechanism 3b). Shipped as a SAFE protective seam:
 * it never demands MORE than {@link UI_SCALE_FLOOR_CEIL} (≤ the tuned L layout
 * scale), so composing it as `max(stored, floor)` can never enlarge text past
 * the readability-layout-net's tuned size — i.e. it cannot overflow a panel. It
 * only guarantees a sane lower bound should a future path lower the stored
 * scale. The real phone legibility win comes from the wider phone rails above.
 * Device-aware activation (raising past L on tiny screens) is intentionally
 * deferred — same staged philosophy as utils/uiScale (shipped dormant).
 */
export const UI_SCALE_FLOOR_FULL = 0.62;
/** Smallest stage scale we tune the floor for (compact phone landscape). */
export const UI_SCALE_FLOOR_MIN = 0.4;
/** The floor never demands more than this (≤ L_UI_SCALE → layout-net-safe). */
export const UI_SCALE_FLOOR_CEIL = 1.0;

export interface StageFitResult {
  /** Uniform scale applied to the design stage to fit the viewport. */
  scale: number;
  /** Viewport-px offset of the scaled stage's top-left (aspect letterbox only). */
  offsetX: number;
  offsetY: number;
  /** Aspect-derived design width; height is always {@link DESIGN_H}. */
  designW: number;
  designH: number;
}

export interface RailWidths {
  left: number;
  right: number;
}

export interface StageLayout extends StageFitResult {
  rails: RailWidths;
  uiScaleFloor: number;
}

const clamp = (v: number, lo: number, hi: number): number =>
  Math.min(Math.max(v, lo), hi);
const lerp = (a: number, b: number, t: number): number => a + (b - a) * t;

/** Clamp a raw width/height ratio into the supported aspect band. */
export function clampAspect(rawAspect: number): number {
  if (!(rawAspect > 0) || !Number.isFinite(rawAspect)) return MIN_ASPECT;
  return clamp(rawAspect, MIN_ASPECT, MAX_ASPECT);
}

/**
 * Fit the fixed-height stage into the FULL viewport ("randlos"). Safe-area
 * insets are NOT reserved — the stage fills `vw × vh` (which, under
 * `viewport-fit=cover`, spans the whole physical display including under the
 * notch / home indicator). The aspect-following width fills with no inner bars
 * while the aspect is in-band; the only residual offset is the aspect letterbox
 * when the device sits outside [{@link MIN_ASPECT}, {@link MAX_ASPECT}].
 */
export function computeStageFit(vw: number, vh: number): StageFitResult {
  const availW = Math.max(0, vw);
  const availH = Math.max(0, vh);
  const aspect = clampAspect(availW / availH);
  const designW = Math.round(DESIGN_H * aspect);
  const scale =
    availW > 0 && availH > 0
      ? Math.min(availW / designW, availH / DESIGN_H)
      : 0;
  const offsetX = Math.round((availW - designW * scale) / 2);
  const offsetY = Math.round((availH - DESIGN_H * scale) / 2);
  return { scale, offsetX, offsetY, designW, designH: DESIGN_H };
}

/**
 * Aspect-pinned rail widths (design px). Piecewise-linear and continuous,
 * anchored so the rails EXACTLY reproduce the signed mockup widths at
 * {@link REF_ASPECT} (the desktop look the owner already approved), shrink to
 * the readable MIN as aspect narrows toward {@link MIN_ASPECT} (recovers the
 * board on iPad), and grow to MAX as aspect widens toward {@link MAX_ASPECT}
 * (absorbs phone side-slack + grants the HUD more px). Input is the
 * fit-derived `designW` (= DESIGN_H × clamped-aspect), so it already carries the
 * clamp — no separate aspect arg can drift from the fit.
 */
export function computeRailWidths(designW: number): RailWidths {
  const aspect = designW / DESIGN_H;
  if (aspect <= MIN_ASPECT) {
    return { left: RAIL_MIN_LEFT, right: RAIL_MIN_RIGHT };
  }
  if (aspect >= MAX_ASPECT) {
    return { left: RAIL_MAX_LEFT, right: RAIL_MAX_RIGHT };
  }
  if (aspect < REF_ASPECT) {
    const t = (aspect - MIN_ASPECT) / (REF_ASPECT - MIN_ASPECT);
    return {
      left: Math.round(lerp(RAIL_MIN_LEFT, RAIL_REF_LEFT, t)),
      right: Math.round(lerp(RAIL_MIN_RIGHT, RAIL_REF_RIGHT, t)),
    };
  }
  const t = (aspect - REF_ASPECT) / (MAX_ASPECT - REF_ASPECT);
  return {
    left: Math.round(lerp(RAIL_REF_LEFT, RAIL_MAX_LEFT, t)),
    right: Math.round(lerp(RAIL_REF_RIGHT, RAIL_MAX_RIGHT, t)),
  };
}

/**
 * The legibility floor for a given stage scale (see {@link UI_SCALE_FLOOR_FULL}).
 * Returns 0 (no demand) on roomy stages and ramps up to
 * {@link UI_SCALE_FLOOR_CEIL} as the stage shrinks toward phone size. Compose as
 * `max(storedUiScale, computeUiScaleFloor(scale))`.
 */
export function computeUiScaleFloor(stageScale: number): number {
  if (!(stageScale > 0) || !Number.isFinite(stageScale)) return 0;
  if (stageScale >= UI_SCALE_FLOOR_FULL) return 0;
  const t = clamp(
    (UI_SCALE_FLOOR_FULL - stageScale) /
      (UI_SCALE_FLOOR_FULL - UI_SCALE_FLOOR_MIN),
    0,
    1,
  );
  return lerp(0, UI_SCALE_FLOOR_CEIL, t);
}

/**
 * Phone DOM-chrome legibility boost (mechanism 4 — the decouple).
 *
 * This is the SEPARATE knob the strategy calls for: it feeds the DOM-only
 * `--chrome-scale` var (see utils/chromeScale + uiTokens.withUiScale), NEVER the
 * shared `getUiScale()` that also drives on-board canvas text. So the phone's
 * chrome can grow for legibility while floating damage numbers / field labels
 * stay put. Deliberately NOT `computeUiScaleFloor`: that floor feeds the shared
 * knob and is capped ≤ 1 (its locked panel-overflow invariant); reusing it would
 * either do nothing (max(L, ≤1) === L) or break that invariant. A fresh ramp
 * with its own ceiling keeps both worlds clean.
 *
 * Returns a MULTIPLIER ≥ 1:
 *   • non-phone tier OR roomy stage (s ≥ {@link CHROME_SCALE_FULL}) → 1 (inert;
 *     tablet/desktop are byte-identical — no boost ever).
 *   • phone tier: ramps 1 → {@link CHROME_SCALE_MAX} as the stage scale shrinks
 *     from {@link CHROME_SCALE_FULL} toward {@link CHROME_SCALE_MIN}.
 *
 * The MAGNITUDE ({@link CHROME_SCALE_MAX}) is the DEVICE-TUNABLE knob. It ships
 * at a conservative 1.5 — enough to lift functional text from the ~8px the phone
 * uniform-scale lands at to the ~11px HIG legibility floor, while staying small
 * enough to minimise rail overflow BEFORE the phone rail-collapse + bottom
 * action bar (Phase 2b/2c) give the boosted chrome more room. Once those land
 * and the layout is validated on a real iPhone, this single constant can be
 * raised toward the owner's 44pt/≈2.5 target (see
 * docs/IPHONE_IPAD_UX_STRATEGY.md §4/§5/§7). Bumping it changes NO gameplay or
 * canvas dimension — it is DOM chrome only.
 */
export const CHROME_SCALE_FULL = 0.62;
/** Smallest phone stage scale the boost ramp is tuned for (compact landscape). */
export const CHROME_SCALE_MIN = 0.36;
/** Maximum DOM-chrome multiplier, reached at/below {@link CHROME_SCALE_MIN}. */
export const CHROME_SCALE_MAX = 1.5;

export function computeChromeScale(
  stageScale: number,
  phoneTier: boolean,
): number {
  if (!phoneTier) return 1;
  if (!(stageScale > 0) || !Number.isFinite(stageScale)) return 1;
  if (stageScale >= CHROME_SCALE_FULL) return 1;
  const t = clamp(
    (CHROME_SCALE_FULL - stageScale) / (CHROME_SCALE_FULL - CHROME_SCALE_MIN),
    0,
    1,
  );
  return lerp(1, CHROME_SCALE_MAX, t);
}

/** Convenience: the full layout (fit + rails + ui-scale floor) in one call. */
export function computeStageLayout(vw: number, vh: number): StageLayout {
  const fit = computeStageFit(vw, vh);
  return {
    ...fit,
    rails: computeRailWidths(fit.designW),
    uiScaleFloor: computeUiScaleFloor(fit.scale),
  };
}
